import React, { useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import OutsideClickHandler from "react-outside-click-handler";
import { useMediaQuery } from "react-responsive";
import { CSSTransition } from "react-transition-group";

import { Dropdown } from "./dropdown";
import Link from "../../link";
import { formatPagePath } from "../../../utils";

import * as styles from "./styles.module.scss";

const Navigation = (props) => {
  const { className, cta = false, intl = false, links = [], onClick } = props;
  const [openDropdown, setOpenDropdown] = useState("main");
  const [openTertiary, setOpenTertiary] = useState("main");

  // ------------------------------------------------------ | Media Query

  const handleQueryChange = (match) => match && setOpenDropdown("main") && setOpenTertiary("main");

  const isMobileNavigation = useMediaQuery(
    { query: "(max-width: 1279px)" },
    undefined,
    handleQueryChange
  );

  // ------------------------------------------------------ | Menu state handlers

  const handleHover = (label) => !isMobileNavigation && handleDropdownClick(label);

  const handleDropdownClick = (label) => {
    label === openDropdown ? setOpenDropdown("main") : setOpenDropdown(label);
    label === openDropdown && setOpenTertiary("main");
  };

  const handleTertiaryClick = (label) => {
    label === openTertiary ? setOpenTertiary("main") : setOpenTertiary(label);
  };

  return (
    <OutsideClickHandler
      onOutsideClick={() => {
        setOpenDropdown("main");
        setOpenTertiary("main");
      }}
    >
      <ul className={classNames(`navigation`, styles.navigation, className)}>
        {links &&
          links?.map((item, idx) => {
            if (item.subnav_links && item.subnav_links.length > 0) {
              // -------------------------------------- | Nav link with tertiary items
              return (
                <li
                  key={`${idx}-${item.id}`}
                  onMouseEnter={() => handleHover(item.linkText || item.link_text)}
                  onMouseLeave={() => handleHover(item.linkText || item.link_text)}
                >
                  <Dropdown
                    key={item.id}
                    openDropdown={openDropdown}
                    openTertiary={openTertiary}
                    handleDropdownClick={handleDropdownClick}
                    handleTertiaryClick={handleTertiaryClick}
                    items={item.subnav_links}
                    label={item.linkText || item.link_text}
                    onClick={onClick}
                    isMobile={isMobileNavigation}
                    articles={item.subnav_cta}
                    intl={intl}
                  />
                </li>
              );
            } else if (item.snippet) {
              // -------------------------------------- | Where to Buy section
              return (
                <CSSTransition
                  in={
                    isMobileNavigation
                      ? openDropdown === "main" || openDropdown === "Where to buy"
                      : true
                  }
                  timeout={0}
                  classNames="menu-primary"
                  unmountOnExit
                  key={`${idx}-${item.id}`}
                >
                  <li key={`${idx}-${item.id}`} className={styles.snippet}>
                    <Dropdown
                      key={item.id}
                      openDropdown={openDropdown}
                      handleDropdownClick={handleDropdownClick}
                      label={"Where to buy"}
                      onClick={onClick}
                      isMobile={isMobileNavigation}
                      snippet={item.snippet}
                      cta={cta}
                    />
                  </li>
                </CSSTransition>
              );
            } else if (cta) {
              // -------------------------------------- | CTA Button
              return (
                <CSSTransition
                  in={
                    isMobileNavigation
                      ? openDropdown === "main" || openDropdown === "Where to buy"
                      : true
                  }
                  timeout={0}
                  classNames="menu-primary"
                  unmountOnExit
                  key={`${idx}-${item.id}`}
                >
                  <li key={`${idx}-${item.id}`}>
                    <Link
                      className={classNames(`btn btn-secondary nav_link`, item.className, {
                        [styles.mobile_only]: item?.mobileOnly
                      })}
                      label={item.linkText || item.link_text}
                      to={item.page ? formatPagePath(item.page) : item.url}
                      onClick={item.onClick}
                      newWindow={item.new_window}
                    >
                      {item.linkText || item.link_text}
                    </Link>
                  </li>
                </CSSTransition>
              );
            } else {
              // -------------------------------------- | Links without children
              return (
                <CSSTransition
                  in={isMobileNavigation ? openDropdown === "main" : true}
                  timeout={0}
                  classNames="menu-primary"
                  unmountOnExit
                  key={`${idx}-${item.id}`}
                >
                  <li key={`${idx}-${item.id}`}>
                    <Link
                      className={classNames(`nav_link`, styles.nav_link, item.className, {
                        [styles.mobile_only]: item?.mobileOnly
                      })}
                      label={item.linkText || item.link_text}
                      to={item.page?.data ? formatPagePath(item.page) : item.url}
                      onClick={item.onClick}
                      newWindow={item.new_window}
                    >
                      {item.linkText || item.link_text}
                    </Link>
                  </li>
                </CSSTransition>
              );
            }
          })}
      </ul>
    </OutsideClickHandler>
  );
};

Navigation.propTypes = {
  /**
   * CSS class(es) applied to the wrapping element
   */
  className: PropTypes.string,
  /**
   * Turns link active state on or off. default: true
   */
  activeState: PropTypes.bool,
  /**
   * Displays Global icon on mobile
   */
  intl: PropTypes.bool,
  /**
   * An array of link objects used to build the list
   */
  links: PropTypes.arrayOf(
    PropTypes.shape({
      className: PropTypes.string,
      link_text: PropTypes.string,
      linkText: PropTypes.string,
      page: PropTypes.object,
      url: PropTypes.string,
      subnav_links: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          link_text: PropTypes.string,
          linkText: PropTypes.string,
          page: PropTypes.object,
          thumb: PropTypes.object,
          url: PropTypes.string
        })
      )
    })
  ).isRequired,
  /**
   * A onClick function passed to all navigation links
   */
  onClick: PropTypes.func,
  cta: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf(["secondary"])])
};

export default Navigation;
